import { combineReducers, configureStore } from '@reduxjs/toolkit'
import { persistStore, persistReducer } from 'redux-persist'
import authReducer from '@/features/auth/authSlice'
import hostTenantReducer from '@/features/auth/hostTenantSlice'
import categoriesReducer from '@/features/categories/categoriesSlice'
import partitionTypesReducer from '@/features/partitions/partitionTypesSlice'
import partitionValuesReducer from '@/features/partitions/partitionValuesSlice'
import metricsReducer from '@/features/metrics/metricsSlice'
import metricEntriesReducer from '@/features/metricEntries/metricEntriesSlice'
import dashboardsReducer from '@/features/dashboards/dashboardsSlice'
import widgetsReducer from '@/features/widgets/widgetsSlice'
import notificationReducer from '@/features/notifications/notificationSlice'
import reportsReducer from '@/features/reports/reportsSlice'
import settingsReducer from '@/features/settings/settingsSlice'
import campusesReducer from '@/features/campuses/campusesSlice'
import subscriptionsReducer from '@/features/subscriptions/subscriptionsSlice'
import formsReducer from '@/features/forms/formsSlice'
import tagsReducer from '@/features/tags/tagsSlice'
import reportTemplatesReducer from '@/features/reportTemplates/reportTemplatesSlice'
import foldersReducer from '@/features/folders/foldersSlice'
import favoritesReducer from '@/features/favorites/favoritesSlice'
import usersReducer from './usersSlice'
import aiChatReducer from '@/features/aiChat/aiChatSlice'
import assistReducer from '@/features/assist/assistSlice'
import suggestionsReducer from '@/features/suggestions/suggestionsSlice'
import freshnessReducer from './freshness'
import { rememberTenantId } from '@/utils/tenantMemory'
import localStorageEngine from './localStorageEngine'
import { withTenantSwitchReset } from './resetOnTenantSwitch'
import { clearQueryCacheOnSessionChange } from './clearQueryCacheOnSessionChange'
import { refreshUsageOnLimitedChange } from './refreshUsageOnLimitedChange'
import { queryClient } from '@/api/queryClient'

/**
 * Redux persist configuration for auth state
 * Only persists essential auth data to localStorage
 */
const persistConfig = {
  key: 'graphit-auth',
  version: 3, // Bump version to force clear of bad data
  storage: localStorageEngine,
  whitelist: ['user', 'currentTenantId', 'tenants', 'isAuthenticated'],
  /**
   * Migrate old persisted state structure to new structure
   * Handles:
   * 1. Transition from 'tenant' (full object) to 'currentTenantId' (ID only)
   * 2. Transition from tenants array to entity adapter format
   */
  migrate: (state) => {
    // If no state, return null to start fresh
    if (!state) {
      return Promise.resolve(null);
    }

    const migratedState = { ...state };

    // Migration 1: Handle old 'tenant' field
    if (migratedState.tenant) {
      migratedState.currentTenantId = migratedState.tenant?.id || null;
      delete migratedState.tenant;
    }

    // Migration 2: Handle tenants array → entity adapter format
    if (Array.isArray(migratedState.tenants)) {
      const tenantsArray = migratedState.tenants;

      // Convert array to entity adapter format
      migratedState.tenants = {
        ids: tenantsArray.map(t => t.id),
        entities: tenantsArray.reduce((acc, tenant) => {
          acc[tenant.id] = tenant;
          return acc;
        }, {})
      };
    }

    // Migration 3: ALWAYS check and fix currentTenantId if it's a tenant_membership ID
    // Tenant IDs are typically short (numeric or short UUID)
    // Tenant membership IDs are long UUIDs with dashes (36+ chars)
    if (migratedState.currentTenantId) {
      const id = String(migratedState.currentTenantId);

      // If it has dashes and is long, it's likely a membership ID
      if (id.includes('-') && id.length >= 36) {
        migratedState.currentTenantId = null;
      }
    }

    // Migration 4: Ensure entity adapter structures exist
    if (!migratedState.tenants || !migratedState.tenants.ids) {
      migratedState.tenants = {
        ids: [],
        entities: {}
      };
    }

    if (!migratedState.tenantMemberships) {
      migratedState.tenantMemberships = {
        ids: [],
        entities: {}
      };
    }

    return Promise.resolve(migratedState);
  }
}

const persistedAuthReducer = persistReducer(persistConfig, authReducer)

/**
 * Every slice combined, before the tenant-switch reset wrapper is applied.
 */
const appReducer = combineReducers({
  auth: persistedAuthReducer,
  // Not persisted: which workspace the host names is re-read on every load.
  hostTenant: hostTenantReducer,
  categories: categoriesReducer,
  partitionTypes: partitionTypesReducer,
  partitionValues: partitionValuesReducer,
  metrics: metricsReducer,
  metricEntries: metricEntriesReducer,
  dashboards: dashboardsReducer,
  widgets: widgetsReducer,
  notification: notificationReducer,
  reports: reportsReducer,
  settings: settingsReducer,
  campuses: campusesReducer,
  subscriptions: subscriptionsReducer,
  forms: formsReducer,
  tags: tagsReducer,
  reportTemplates: reportTemplatesReducer,
  folders: foldersReducer,
  favorites: favoritesReducer,
  users: usersReducer,
  aiChat: aiChatReducer,
  assist: assistReducer,
  suggestions: suggestionsReducer,
  freshness: freshnessReducer
})

/**
 * Resets every tenant-scoped entity slice back to its initial state on a
 * completed tenant switch, so switching in-session (TenantSelectPage, which
 * unlike UserMenu/TenantSwitcher does not force a page reload) can't leave
 * the previous tenant's categories/campuses/dashboards/etc. rendering until
 * something happens to refetch them. `auth` is exempted - it's not
 * tenant-scoped entity data, it's what just performed the switch - and so is
 * `hostTenant`, whose apply step is itself a switch.
 */
const rootReducer = withTenantSwitchReset(appReducer)

/**
 * Configure Redux store with persisted auth reducer
 * @type {import('@reduxjs/toolkit').Store}
 */
export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: ['persist/PERSIST', 'persist/REHYDRATE']
      }
    }).concat(clearQueryCacheOnSessionChange(queryClient), refreshUsageOnLimitedChange)
})

/**
 * Redux persistor for rehydrating state
 */
export const persistor = persistStore(store)

// Mirror the active tenant to its own localStorage key whenever it changes, so
// a multi-tenant user is returned to their last organization next session
// (#167). Centralized here so every path that sets the tenant — manual switch,
// single-tenant auto-select, signup — is covered without touching each one.
// Only non-null values are written; logout leaves the last choice remembered,
// and it's re-validated against the membership list on the next sign-in.
let lastRememberedTenantId = store.getState().auth?.currentTenantId ?? null
// A tenant pinned by a superuser's impersonation is not the admin's own
// choice, so it is never remembered: exiting returns them to their last real
// organization.
store.subscribe(() => {
  const { currentTenantId = null, impersonation } = store.getState().auth ?? {}
  if (currentTenantId !== lastRememberedTenantId) {
    lastRememberedTenantId = currentTenantId
    if (!impersonation?.active) rememberTenantId(currentTenantId)
  }
})

/**
 * @typedef {ReturnType<typeof store.getState>} RootState
 * @typedef {typeof store.dispatch} AppDispatch
 */
